import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { SESSION_TYPES } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  email: "",
  phone: "",
  session_type: SESSION_TYPES[0],
  preferred_date: "",
  location: "",
  message: "",
};

const Booking = () => {
  const [form, setForm] = useState(initialState);
  const [status, setStatus] = useState({ state: "idle", error: "" });

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "submitting", error: "" });
    try {
      await axios.post(`${API}/bookings`, form);
      setStatus({ state: "success", error: "" });
      setForm(initialState);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      setStatus({ state: "error", error: String(detail) });
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="bg-[#F9F8F6] px-6 md:px-10 lg:px-16 py-24 md:py-36"
    >
      <div className="mx-auto max-w-[1500px] grid grid-cols-12 gap-8 md:gap-16">
        {/* Left text */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
          className="col-span-12 md:col-span-5"
        >
          <p className="overline mb-5">Schedule a Session</p>
          <h2
            data-testid="booking-heading"
            className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-[#2A2A2A]"
          >
            Let's plan something <br />
            <span className="italic text-[#C48B71]">beautiful</span>.
          </h2>
          <p className="mt-8 text-[#5C5C5C] text-[1.02rem] leading-[1.8] font-light max-w-md">
            Tell me a little about what you have in mind. I read every inquiry
            personally and reply within 48 hours — no auto-responders, no
            templates.
          </p>
          <div className="mt-10 space-y-4 text-[#2A2A2A]">
            <div>
              <p className="overline">Response time</p>
              <p className="serif text-2xl">Within 48 hours</p>
            </div>
            <div>
              <p className="overline">Booking window</p>
              <p className="serif text-2xl">2026 · 2027</p>
            </div>
          </div>
        </motion.div>

        {/* Right form */}
        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          onSubmit={onSubmit}
          data-testid="booking-form"
          className="col-span-12 md:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
        >
          <div className="md:col-span-1">
            <label className="overline block mb-1">Your name</label>
            <input
              required
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="Jane Doe"
              data-testid="booking-input-name"
              className="input-editorial"
            />
          </div>
          <div className="md:col-span-1">
            <label className="overline block mb-1">Email</label>
            <input
              required
              type="email"
              name="email"
              value={form.email}
              onChange={onChange}
              placeholder="jane@example.com"
              data-testid="booking-input-email"
              className="input-editorial"
            />
          </div>
          <div className="md:col-span-1">
            <label className="overline block mb-1">Phone (optional)</label>
            <input
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder="+1 555 123 4567"
              data-testid="booking-input-phone"
              className="input-editorial"
            />
          </div>
          <div className="md:col-span-1">
            <label className="overline block mb-1">Session type</label>
            <select
              name="session_type"
              value={form.session_type}
              onChange={onChange}
              data-testid="booking-input-session"
              className="input-editorial bg-transparent"
            >
              {SESSION_TYPES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-1">
            <label className="overline block mb-1">Preferred date</label>
            <input
              required
              type="date"
              name="preferred_date"
              value={form.preferred_date}
              onChange={onChange}
              data-testid="booking-input-date"
              className="input-editorial"
            />
          </div>
          <div className="md:col-span-1">
            <label className="overline block mb-1">Location (optional)</label>
            <input
              name="location"
              value={form.location}
              onChange={onChange}
              placeholder="Denver, CO"
              data-testid="booking-input-location"
              className="input-editorial"
            />
          </div>
          <div className="md:col-span-2">
            <label className="overline block mb-1">Tell me about it</label>
            <textarea
              name="message"
              value={form.message}
              onChange={onChange}
              rows={4}
              placeholder="A little about you, your people, and the kind of session you're hoping for…"
              data-testid="booking-input-message"
              className="input-editorial resize-none"
            />
          </div>

          <div className="md:col-span-2 flex flex-wrap items-center gap-5 pt-3">
            <button
              type="submit"
              disabled={status.state === "submitting"}
              data-testid="booking-submit"
              className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status.state === "submitting" && (
                <Loader2 size={16} className="animate-spin" />
              )}
              {status.state === "success" ? "Sent — thank you" : "Send Inquiry"}
            </button>

            {status.state === "success" && (
              <p
                data-testid="booking-success"
                className="overline text-[#2A2A2A] flex items-center gap-2"
              >
                <Check size={14} className="text-[#C48B71]" />
                I'll be in touch within 48 hours.
              </p>
            )}
            {status.state === "error" && (
              <p
                data-testid="booking-error"
                className="overline text-red-700"
              >
                {status.error}
              </p>
            )}
          </div>
        </motion.form>
      </div>
    </section>
  );
};

export default Booking;
